import { useMemo } from 'react';

import {
    type FishGroupPopulationInfo,
    type OverviewCage,
    type OverviewLocation,
    useFishGroupPopulationContext,
    useGetClientFishGroupsOverview,
} from '@/domain/fishGroups';

import {
    type CageWarning,
    computeCageWarnings,
    validateDestinations,
    type WizardValidationError,
} from '../operateWizardHelpers';
import type { OperateWizardState } from '../operateWizardReducer';

export interface OperateWizardData {
    isLoading: boolean;
    hasError: boolean;
    /** The cages at the wizard's location, empty until the overview has loaded. */
    locationCages: OverviewCage[];
    validationErrors: WizardValidationError[];
    cageWarnings: CageWarning[];
    operationMinDate: Date | null;
    populationById: Map<number, FishGroupPopulationInfo>;
}

/** What the wizard derives from its state and the client's fish group overview. */
export function useOperateWizardData(
    clientId: number,
    state: OperateWizardState
): OperateWizardData {
    const { data: overview, isLoading, error } = useGetClientFishGroupsOverview(clientId);

    const currentLocation = useMemo<OverviewLocation | null>(() => {
        if (!overview) return null;
        const all = [...overview.activeLocations, ...overview.inactiveLocations];
        return all.find((loc) => loc.locationId === state.locationId) ?? null;
    }, [overview, state.locationId]);

    const locationCages: OverviewCage[] = currentLocation?.cages ?? [];

    const validationErrors: WizardValidationError[] = useMemo(
        () => validateDestinations({ sources: state.sources, destinations: state.destinations }),
        [state.sources, state.destinations]
    );

    const cageWarnings: CageWarning[] = useMemo(
        () =>
            computeCageWarnings({
                sources: state.sources,
                destinations: state.destinations,
                locationCages,
            }),
        [state.sources, state.destinations, locationCages]
    );

    /**
     * Lower bound for the operation date picker. The DB rejects any
     * operation whose date is not strictly after the active location's
     * `fromDate` of every source. The overview only exposes
     * `lifecycleStartedAt` per active FG (the FG's birth date, not the
     * current cage placement date), so for a freshly-created source they
     * line up — and for a relocated source the picker is laxer than the
     * backend by design: the user can still pick an invalid date in the
     * gap between lifecycle start and the latest relocation, and the
     * backend's `INVALID_OPERATION_DATE` will surface it as a clean 400.
     * Looking up the exact `currentLocation.fromDate` per source would
     * require fetching detail per FG (N requests) — deferred to a future
     * iteration if the relocated-source case turns out to be common.
     */
    const operationMinDate: Date | null = useMemo(() => {
        let max: number | null = null;
        for (const source of state.sources) {
            const cage = locationCages.find((c) => c.cageId === source.currentCageId);
            const isoDate = cage?.activeFishGroup?.lifecycleStartedAt ?? null;
            if (!isoDate) continue;
            const ms = Date.parse(isoDate);
            if (!Number.isFinite(ms)) continue;
            if (max === null || ms > max) max = ms;
        }
        return max !== null ? new Date(max) : null;
    }, [state.sources, locationCages]);

    const sourceIds = useMemo(() => state.sources.map((s) => s.fishGroupId), [state.sources]);
    const { byId: populationById } = useFishGroupPopulationContext(clientId, sourceIds);

    return {
        isLoading,
        hasError: Boolean(error),
        locationCages,
        validationErrors,
        cageWarnings,
        operationMinDate,
        populationById,
    };
}
